import { VerticalBox } from "src/components/common/box/VerticalBox";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import i18n from "i18next";
import { faCheck, faInputText, faQrcode, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Box, Modal, Typography } from "@mui/material";
import { concatWithPipe, toIntOrNull } from "src/utils/strings.tsx";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { DeliveryPackageBundleCreationApi } from "src/api/generated/erp/delivery/api/deliveryPackageBundleCreationApi.ts";
import React, { useState } from "react";
import { useFormInput } from "src/components/common/dialogs/formInput/useFormInput.tsx";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { QrcodeReader } from "src/components/common/qrCode/QrcodeReader.tsx";
import { DeliveryPackageApi } from "src/api/generated/erp/delivery/api/deliveryPackageApi.ts";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { useMaybeStoredState } from "src/utils/useMaybeStoredState.ts";
import { Address } from "src/api/generated/erp/db/types/embeddables/address.ts";
import {
	getDeliveryPackageAddress,
} from "src/components/views/erp/delivery/deliveryPackageSourceUtils.ts";
import { DeliveryPackageWithSourcesView } from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";
import equal from "fast-deep-equal/es6";
import {
	DeliveryPackageSourceType
} from "src/api/generated/io/aavo/applications/db/erp/types/deliveryPackageSourceType.ts";

export interface DeliveryPackageToBundleSelectionViewProps {
	initialDeliveryAddress?: Address;
	initialSelectedPackageIds?: number[];
	storageKey?: string; // If not set, no storage is used
	sourceType?: DeliveryPackageSourceType;
	onCompleted: (params: DeliveryPackageToBundleSelectionViewOnCompletedParams) => Promise<unknown>;
	extraActions?: React.ReactNode;
}

export interface DeliveryPackageToBundleSelectionViewOnCompletedParams {
	selectedPackages: DeliveryPackageWithSourcesView[];
}

export const DeliveryPackageToBundleSelectionView = (props: DeliveryPackageToBundleSelectionViewProps) => {
	const { initialDeliveryAddress, initialSelectedPackageIds, storageKey } = props;

	const [storedSelectedPackageIds, setStoredSelectedPackageIds] = useMaybeStoredState({
		defaultValue: initialSelectedPackageIds ?? [],
		key: storageKey ? `${storageKey}__selected_package_ids` : undefined,
		type: "array",
	});
	const [storedDeliveryAddress, setStoredDeliveryAddress] = useMaybeStoredState<Address | null>({
		defaultValue: initialDeliveryAddress ?? null,
		key: storageKey ? `${storageKey}__delivery_address` : undefined,
		type: "object",
	});

	return (
		<AsyncFetchRender
			fetch={async () => {
				if (storedSelectedPackageIds.length === 0) return [];
				return await DeliveryPackageBundleCreationApi.getNonBundledDeliveryPackages({
					deliveryPackageIds: storedSelectedPackageIds,
				});
			}}
			content={(packages: DeliveryPackageWithSourcesView[]) => (
				<AsyncContent
					initialPackages={packages}
					storeSelectedPackages={setStoredSelectedPackageIds}
					deliveryAddress={storedDeliveryAddress}
					setDeliveryAddress={setStoredDeliveryAddress}
					resetDeliveryAddress={() => {
						setStoredDeliveryAddress(initialDeliveryAddress ?? null);
					}}
					{...props}
				/>
			)}
		/>
	);
};

interface AsyncContentProps
	extends Pick<DeliveryPackageToBundleSelectionViewProps, "onCompleted" | "sourceType" | "extraActions"> {
	deliveryAddress: Address | null;
	setDeliveryAddress: (source: Address | null) => void;
	resetDeliveryAddress: () => void;
	initialPackages: DeliveryPackageWithSourcesView[];
	storeSelectedPackages: (packageIds: number[]) => void;
}

const AsyncContent = ({
	deliveryAddress,
	setDeliveryAddress,
	resetDeliveryAddress,
	initialPackages,
	onCompleted,
	sourceType,
	extraActions,
	storeSelectedPackages,
}: AsyncContentProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const showMessageDialog = useMessageDialog();
	const showFormInput = useFormInput();

	const { defaultSiteId } = useGlobalInitData();

	const [packages, setPackages] = useState<DeliveryPackageWithSourcesView[]>(initialPackages ?? []);
	const [qrCodeReaderOpen, setQrCodeReaderOpen] = useState(false);

	return (
		<VerticalBox
			sx={{
				flex: 1,
				padding: 2,
				gap: 2,
				alignItems: "stretch",
			}}
		>
			<Typography variant={"h6"}>{i18n.t("packages")}</Typography>
			<VerticalBox
				sx={{
					overflowY: "auto",
					marginBottom: 2,
				}}
			>
				{packages.length === 0 ?
					<Typography>- {i18n.t("no_packages_selected")} -</Typography>
				:	packages.map((pkg) => (
						<PackageItem
							key={pkg.deliveryPackageId}
							pkg={pkg}
							remove={() => removePackage(pkg)}
						/>
					))
				}
			</VerticalBox>
			<VerticalBox sx={{ flex: 1 }} />
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: 1,
				}}
			>
				{extraActions}
				<AsyncButton
					icon={faXmark}
					label={i18n.t("clear")}
					variant={"outlined"}
					disabled={packages.length === 0}
					onClick={() => {
						setPackagesWrapped([]);
						setDeliveryAddress(null);
					}}
					size={"large"}
					sx={{
						flex: 1,
						gridColumnStart: 1,
					}}
				/>
				<AsyncButton
					icon={faInputText}
					label={i18n.t("select_package")}
					variant={"outlined"}
					onClick={onSelectPackageClick}
					size={"large"}
					sx={{
						flex: 1,
					}}
				/>
				<AsyncButton
					icon={faCheck}
					label={i18n.t("finish.as_complete")}
					variant={"contained"}
					size={"large"}
					disabled={packages.length === 0}
					onClick={onFinish}
				/>
				<AsyncButton
					icon={faQrcode}
					label={i18n.t("scan_package")}
					variant={"contained"}
					size={"large"}
					onClick={() => {
						setQrCodeReaderOpen(true);
					}}
				/>
			</Box>
			<Modal
				open={qrCodeReaderOpen}
				onClose={() => setQrCodeReaderOpen(false)}
				onClick={() => setQrCodeReaderOpen(false)}
			>
				<QrcodeReader
					onQrCodeRead={async (qrCodeContent) => {
						setQrCodeReaderOpen(false);
						await onQrCodeRead(qrCodeContent);
					}}
				/>
			</Modal>
		</VerticalBox>
	);

	function setPackagesWrapped(newPackages: DeliveryPackageWithSourcesView[]) {
		setPackages(newPackages);
		storeSelectedPackages(newPackages.map((p) => p.deliveryPackageId));
	}

	async function onQrCodeRead(qrCodeContent: string) {
		try {
			const packageId = toIntOrNull(qrCodeContent);
			if (packageId == null) {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("the_scanned_code_is_not_a_valid_package_code", { code: qrCodeContent }),

				});
				return;
			}
			if (packages.find((p) => p.deliveryPackageId === packageId)) {
				showMessageDialog({
					title: i18n.t("package_already_added"),
					content: i18n.t("this_package_is_already_added"),
				});
				return;
			}
			const packageToAdd = await DeliveryPackageApi.getDeliveryPackageOrNull({
				deliveryPackageId: packageId,
			});
			if (packageToAdd == null) {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("no_package_found_for_scanned_code", { code: qrCodeContent }),
				});
				return;
			}

			if (packageToAdd.state === "DELIVERED") {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("package_has_already_been_delivered"),
				});
				return;
			}

			if (packageToAdd.isBundle) {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("delivery.bundles_cannot_be_added_to_bundles"),
				});
				return;
			}

			if (packageToAdd.parentPackageBundleId != null) {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("delivery.package_is_already_in_a_bundle"),
				});
				return;
			}

			const newPackageAddress = getDeliveryPackageAddress(packageToAdd);
			if (deliveryAddress != null && !equal(newPackageAddress, deliveryAddress)) {
				showMessageDialog({
					title: i18n.t("error"),
					content: i18n.t("delivery.package_source_address_is_different"),
				});
				return;
			}

			addPackage(packageToAdd);
		} catch (e) {
			logErrorAndShowOnDialog(e);
		}
		throw new Error("Function not implemented.");
	}

	async function onSelectPackageClick() {
		interface FormInputValues {
			selectedPackage: DeliveryPackageWithSourcesView;
		}

		const formInputValues = await showFormInput<FormInputValues>({
			title: i18n.t("add_package"),
			defaultValues: {
				selectedPackage: undefined,
			},
			submitLabel: i18n.t("ok"),
			content: ({ control }) => (
				<>
					<FormAsyncSelectField
						control={control}
						name={"selectedPackage"}
						formValueType={"option"}
						autoFocus
						fetchOptions={async ({ searchQuery, currentSelection }) => {
							const packageOptions = await DeliveryPackageBundleCreationApi.getDeliveryPackageOptions({
								siteId: defaultSiteId,
								searchQuery: searchQuery,
								currentSelection: currentSelection,
								deliveryAddress: deliveryAddress,
								sourceType: sourceType,
							});
							return packageOptions.filter(
								(pkg) => !packages.find((p) => p.deliveryPackageId === pkg.deliveryPackageId),
							);
						}}
						label={i18n.t("select_a_package")}
						getOptionKey={(pkg: DeliveryPackageWithSourcesView) => pkg.deliveryPackageId}
						getOptionLabel={(pkg) =>
							concatWithPipe(pkg.sourceDescriptions, pkg.packageNo, pkg.packageTypeName, pkg.description)
						}
					/>
				</>
			),
		});

		if (formInputValues?.selectedPackage == undefined) return;

		addPackage(formInputValues.selectedPackage);
	}

	function addPackage(packageToAdd: DeliveryPackageWithSourcesView) {
		if (packages.find((p) => p.deliveryPackageId === packageToAdd.deliveryPackageId)) return;

		const newPackages = [...packages, packageToAdd];
		setPackagesWrapped(newPackages);

		// Define delivery address by first selected package if not yet defined
		if (deliveryAddress == null) {
			const address = getDeliveryPackageAddress(packageToAdd);
			setDeliveryAddress(address ?? null);
		}
	}

	function removePackage(pkg: DeliveryPackageWithSourcesView) {
		const newPackages = packages.filter((p) => p.deliveryPackageId !== pkg.deliveryPackageId);
		setPackagesWrapped(newPackages);
		if (newPackages.length === 0) {
			resetDeliveryAddress();
		}
	}

	async function onFinish() {
		if (deliveryAddress == null) return; // Should not happen, button is disabled

		await onCompleted({
			selectedPackages: packages,
		});
		resetDeliveryAddress();
		setPackagesWrapped([]);
	}
};

interface PackageItemProps {
	pkg: DeliveryPackageWithSourcesView;
	remove: () => void;
}

const PackageItem = ({ pkg, remove }: PackageItemProps) => {
	return (
		<HorizontalBox
			sx={{
				alignItems: "center",
				gap: 1,
				borderColor: "divider",
				borderBottom: "1px solid",
			}}
		>
			<Typography>
				{concatWithPipe(
					pkg.sourceDescriptions,
					pkg.packageNo,
					pkg.packageTypeName,
					pkg.description,
				)}
			</Typography>
			<AsyncButton icon={faXmark} onClick={remove} color={"secondary"} />
		</HorizontalBox>
	);
};
